import { Assert, jsonPointerGet } from '@cotera/utilities';
import {
  isAddressableUpstreamFieldPath,
  normalizeUpstreamFieldPath,
} from './upstream-field-path';
// type-fest's JsonValue (not the SDK's `serializable` one, whose `{}` arm makes
// it unassignable to the server's type-fest JsonValue): the flush envelope and
// reducer outputs cross into the server saga / kv layer, which is type-fest.
import type { JsonValue } from 'type-fest';

/**
 * A bucket node accumulates the output of many runs of the same workflow and,
 * when its flush policy fires, runs its downstream branch once with the combined
 * data. This module holds the framework-free policy + reducer logic so the
 * server runner/evaluator and the client editor share one source of truth.
 */

/** How the flush triggers combine. Only `or` is supported today (see `evaluateBucketPolicy`). */
export const BUCKET_COMBINATORS = ['or', 'and'] as const;
export type BucketCombinator = (typeof BUCKET_COMBINATORS)[number];

/** Flush when at least `threshold` items have accumulated since the last flush. */
export type BucketCountTrigger = {
  readonly kind: 'count';
  readonly threshold: number;
};

/**
 * Flush on a cron schedule — the same `cron`/`timezone` a workflow cron trigger
 * uses. A per-bucket Restate cron object fires the flush at these times, so a
 * time flush is a scheduled "digest" (e.g. daily at 9am) rather than an elapsed
 * interval.
 */
export type BucketTimeTrigger = {
  readonly kind: 'time';
  readonly cron: string;
  readonly timezone: string;
};

export type BucketFlushTrigger = BucketCountTrigger | BucketTimeTrigger;

/**
 * A bucket's flush policy: a list of triggers. Count triggers combine by
 * `combinator`; a time (cron) trigger fires independently on its schedule. The
 * canonical example is "every 500 items OR daily at 9am".
 */
export type BucketFlushPolicy = {
  readonly combinator: BucketCombinator;
  readonly triggers: readonly BucketFlushTrigger[];
};

/** Aggregations a reducer can compute over the accumulated items at a pointer. */
export const BUCKET_REDUCER_OPS = [
  'sum',
  'avg',
  'min',
  'max',
  'first',
  'last',
  'count',
  'distinct',
] as const;
export type BucketReducerOp = (typeof BUCKET_REDUCER_OPS)[number];

/** One named aggregation over the accumulated items, surfaced under `reduced`. */
export type BucketReducer = {
  readonly op: BucketReducerOp;
  /** RFC 6901 JSON Pointer into each item (`''` = the whole item). */
  readonly path: string;
  /** Output key under the flush envelope's `reduced` object. */
  readonly as: string;
};

/** Persisted bucket node `data`. */
export type BucketConfig = {
  readonly policy: BucketFlushPolicy;
  /**
   * Optional field path selecting what to store per contribution (default: the
   * whole upstream value). A bare JSON Pointer reads this step's own input; a
   * `Node/pointer` reference reads that ancestor's output.
   */
  readonly accumulate?: string;
  readonly reducers?: readonly BucketReducer[];
};

export type ParseBucketConfigResult =
  | { readonly ok: true; readonly config: BucketConfig }
  | { readonly ok: false; readonly error: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function isBucketReducerOp(value: unknown): value is BucketReducerOp {
  return (
    typeof value === 'string' &&
    (BUCKET_REDUCER_OPS as readonly string[]).includes(value)
  );
}

export function isBucketCombinator(value: unknown): value is BucketCombinator {
  return value === 'or' || value === 'and';
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function parseTrigger(
  data: unknown,
  prefix: string
): { ok: true; trigger: BucketFlushTrigger } | { ok: false; error: string } {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: `${prefix}trigger must be an object.` };
  }
  const kind = record['kind'];
  if (kind === 'count') {
    const threshold = record['threshold'];
    if (!isPositiveInteger(threshold)) {
      return {
        ok: false,
        error: `${prefix}count trigger needs a positive whole-number threshold.`,
      };
    }
    return { ok: true, trigger: { kind: 'count', threshold } };
  }
  if (kind === 'time') {
    const cron = record['cron'];
    if (typeof cron !== 'string' || cron.trim() === '') {
      return {
        ok: false,
        error: `${prefix}time trigger needs a cron schedule.`,
      };
    }
    const timezone = record['timezone'];
    const resolvedTimezone =
      typeof timezone === 'string' && timezone.trim() !== '' ? timezone : 'UTC';
    return {
      ok: true,
      trigger: { kind: 'time', cron, timezone: resolvedTimezone },
    };
  }
  return {
    ok: false,
    error: `${prefix}trigger has an unknown kind: ${String(kind)}`,
  };
}

function parseReducer(
  data: unknown,
  prefix: string
): { ok: true; reducer: BucketReducer } | { ok: false; error: string } {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: `${prefix}reducer must be an object.` };
  }
  const op = record['op'];
  if (!isBucketReducerOp(op)) {
    return {
      ok: false,
      error: `${prefix}reducer has an unknown op: ${String(op)}`,
    };
  }
  const rawPath = record['path'];
  if (rawPath !== undefined && typeof rawPath !== 'string') {
    return { ok: false, error: `${prefix}reducer path must be a string.` };
  }
  const path = rawPath ?? '';
  // Pointer-only, unlike `accumulate`, and not an oversight. A reducer runs at
  // flush time in `applyBucketReducers`, over items already stored — often woken
  // by cron, with no run and so no ancestor outputs in scope. There is nothing
  // for `Node/field` to resolve against, so it is rejected rather than accepted
  // and silently read as `undefined`.
  if (path !== '' && !path.startsWith('/')) {
    return {
      ok: false,
      error: `${prefix}reducer path must be a JSON Pointer ("/field") or blank. Reducers read each stored item, so they cannot reference another node.`,
    };
  }
  const as = record['as'];
  if (typeof as !== 'string' || as.trim() === '') {
    return {
      ok: false,
      error: `${prefix}reducer needs a non-empty output name.`,
    };
  }
  return { ok: true, reducer: { op, path, as } };
}

/**
 * Validates persisted bucket node `data` into a usable {@link BucketConfig}, or
 * returns a clear error so callers can fail the step safely rather than guessing.
 */
export function parseBucketConfig(data: unknown): ParseBucketConfigResult {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: 'Bucket node is missing configuration.' };
  }

  const policyRecord = asRecord(record['policy']);
  if (policyRecord === null) {
    return { ok: false, error: 'Bucket node is missing a flush policy.' };
  }

  const rawTriggers = policyRecord['triggers'];
  if (!Array.isArray(rawTriggers) || rawTriggers.length === 0) {
    return {
      ok: false,
      error: 'Bucket node needs at least one flush trigger.',
    };
  }
  const triggers: BucketFlushTrigger[] = [];
  for (let i = 0; i < rawTriggers.length; i++) {
    const parsed = parseTrigger(rawTriggers[i], `Trigger ${i + 1}: `);
    if (!parsed.ok) {
      return { ok: false, error: parsed.error };
    }
    triggers.push(parsed.trigger);
  }

  const combinator = policyRecord['combinator'];
  if (combinator !== undefined && !isBucketCombinator(combinator)) {
    return { ok: false, error: 'Bucket combinator must be "or" or "and".' };
  }

  const rawAccumulate = record['accumulate'];
  if (rawAccumulate !== undefined && typeof rawAccumulate !== 'string') {
    return { ok: false, error: 'Bucket accumulate path must be a string.' };
  }
  // The same two forms a debounce key or a filter field takes: a bare pointer
  // into this step's own input, or `Node/pointer` naming an ancestor to read
  // from. `undefined` stays `undefined` — it means "the whole upstream value",
  // which is not the same as an empty string the author typed.
  const accumulate =
    rawAccumulate === undefined
      ? undefined
      : normalizeUpstreamFieldPath(rawAccumulate);
  if (accumulate !== undefined && !isAddressableUpstreamFieldPath(accumulate)) {
    return {
      ok: false,
      error: `Bucket accumulate path must be a JSON Pointer ("/field"), an upstream reference ("Node/field"), or blank (got "${accumulate}").`,
    };
  }

  const reducers: BucketReducer[] = [];
  const rawReducers = record['reducers'];
  if (rawReducers !== undefined && rawReducers !== null) {
    if (!Array.isArray(rawReducers)) {
      return { ok: false, error: 'Bucket reducers must be a list.' };
    }
    for (let i = 0; i < rawReducers.length; i++) {
      const parsed = parseReducer(rawReducers[i], `Reducer ${i + 1}: `);
      if (!parsed.ok) {
        return { ok: false, error: parsed.error };
      }
      reducers.push(parsed.reducer);
    }
  }

  return {
    ok: true,
    config: {
      policy: {
        combinator: isBucketCombinator(combinator) ? combinator : 'or',
        triggers,
      },
      ...(accumulate !== undefined && { accumulate }),
      ...(reducers.length > 0 && { reducers }),
    },
  };
}

/** Why a flush fired, surfaced in the flush envelope's `window.reason`. */
export type BucketFlushReason = 'count' | 'time' | 'manual' | 'group';

export type BucketFlushDecision =
  | { readonly flush: false }
  | {
      readonly flush: true;
      readonly reason: BucketFlushReason;
      /**
       * How many of the oldest pending items to claim for this flush. A count
       * flush takes exactly one batch (the smallest satisfied threshold) so a
       * burst of items produces even batches rather than one big flush; a time or
       * manual flush drains the whole window (`undefined` = all pending).
       */
      readonly limit?: number;
    };

/** What asked the bucket to evaluate a flush. */
export type BucketFlushSource = 'count' | 'time' | 'manual' | 'group';

/**
 * Decides whether a bucket should flush right now, given what triggered the
 * evaluation. Each source is its own path: a contribution evaluates `count`, a
 * cron tick evaluates `time`, a settled run group evaluates `group`, and a user
 * drain evaluates `manual`.
 *
 * - `count`: flush when a count trigger's threshold is met, taking exactly one
 *   batch (the smallest satisfied threshold) so bursts flush evenly.
 * - `time`: a cron tick means "flush the window now" — drain all pending.
 * - `group`: the batch of work feeding this bucket has finished. This RESPECTS
 *   the configured policy — it evaluates exactly as `count` does, so a settled
 *   batch (even a batch of one) never forces a partial digest a bucket set to
 *   "every N" did not ask for; a below-threshold remainder rolls into the next
 *   accumulation. A bucket follows the setting it was given, nothing more. (The
 *   "import finished, send the summary now" case — flushing the remainder on
 *   batch completion — is intentionally not covered here; it will arrive as an
 *   explicit, opt-in flush setting rather than an invisible override.)
 * - `manual`: drain all pending regardless (honored even when empty).
 *
 * Only `manual` fires an empty bucket. For the other three, flushing nothing
 * would spawn a pointless downstream run — and for `group` in particular it
 * would fire once per settled batch whether or not anything reached the node.
 */
export function evaluateBucketPolicy(params: {
  readonly policy: BucketFlushPolicy;
  readonly pendingCount: number;
  readonly source: BucketFlushSource;
}): BucketFlushDecision {
  const { policy, pendingCount, source } = params;

  if (source === 'manual') {
    return { flush: true, reason: 'manual' };
  }
  if (pendingCount <= 0) {
    return { flush: false };
  }
  if (source === 'time') {
    // A cron tick means "flush the window now" — drain the whole accumulated
    // window with no batch limit.
    return { flush: true, reason: 'time' };
  }

  // Count path — also the path a settled `group` takes, so a finished batch
  // flushes only what the policy's own triggers would. Fire on the count
  // triggers, combined by the policy's combinator.
  const firedCount = policy.triggers.filter(
    (t): t is BucketCountTrigger =>
      t.kind === 'count' && pendingCount >= t.threshold
  );
  const countTriggers = policy.triggers.filter((t) => t.kind === 'count');
  if (countTriggers.length === 0) {
    return { flush: false };
  }
  const passes =
    policy.combinator === 'and'
      ? firedCount.length === countTriggers.length
      : firedCount.length > 0;
  if (!passes) {
    return { flush: false };
  }
  // Take exactly one batch — the smallest satisfied threshold.
  const limit = Math.min(...firedCount.map((t) => t.threshold));
  return { flush: true, reason: 'count', limit };
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
      return Number(trimmed);
    }
  }
  return undefined;
}

function reduceOne(op: BucketReducerOp, values: readonly unknown[]): JsonValue {
  switch (op) {
    case 'count':
      return values.length;
    case 'first':
      return (values[0] ?? null) as JsonValue;
    case 'last':
      return (values[values.length - 1] ?? null) as JsonValue;
    case 'distinct': {
      const seen = new Set<string>();
      for (const value of values) {
        seen.add(JSON.stringify(value ?? null));
      }
      return seen.size;
    }
    case 'sum':
    case 'avg':
    case 'min':
    case 'max': {
      const numbers = values
        .map(toNumber)
        .filter((n): n is number => n !== undefined);
      if (numbers.length === 0) {
        return null;
      }
      if (op === 'sum') {
        return numbers.reduce((a, b) => a + b, 0);
      }
      if (op === 'avg') {
        return numbers.reduce((a, b) => a + b, 0) / numbers.length;
      }
      if (op === 'min') {
        return Math.min(...numbers);
      }
      return Math.max(...numbers);
    }
    default:
      return Assert.unreachable(op);
  }
}

/** Computes the `reduced` map for a flush from the accumulated items. */
export function applyBucketReducers(
  items: readonly JsonValue[],
  reducers: readonly BucketReducer[]
): Record<string, JsonValue> {
  const reduced: Record<string, JsonValue> = {};
  for (const reducer of reducers) {
    const values = items.map((item) => jsonPointerGet(item, reducer.path));
    reduced[reducer.as] = reduceOne(reducer.op, values);
  }
  return reduced;
}

/** The combined payload a flush run receives as the bucket node's output. */
export type BucketFlushEnvelope = {
  readonly items: readonly JsonValue[];
  readonly count: number;
  readonly reduced?: Record<string, JsonValue>;
  readonly window: {
    readonly from: string | null;
    readonly to: string;
    readonly flushedAt: string;
    readonly reason: BucketFlushReason;
  };
};

/** Builds the flush envelope from the claimed items and the flush metadata. */
export function buildBucketFlushEnvelope(params: {
  readonly items: readonly JsonValue[];
  readonly reducers?: readonly BucketReducer[];
  readonly reason: BucketFlushReason;
  readonly from: string | null;
  readonly to: string;
  readonly flushedAt: string;
}): BucketFlushEnvelope {
  const { items, reducers, reason, from, to, flushedAt } = params;
  return {
    items,
    count: items.length,
    ...(reducers !== undefined &&
      reducers.length > 0 && {
        reduced: applyBucketReducers(items, reducers),
      }),
    window: { from, to, flushedAt, reason },
  };
}
